import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';
import _ from 'underscore';

import {removeGaps} from 'biojs/Util';
import {AllPolymerLayouts} from 'constants/MapsConstants';
import {getRealToGapMap} from 'molbio/aa-sequences/alignments/getRealToGapMap';
import antibodyAnalysisStore from 'molbio/antibody-engineering/antibodyAnalysis';
import {
  CDRAnnotation,
  CDRDefinition,
  LIABILITY_SITE_DISPLAY_NAMES,
  LiabilitySiteType,
  PTMSite,
} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import {getAntibodyVisibilitySettings} from 'molbio/antibody-engineering/visibilityHelpers';
import {getPostTranslationalModifications} from 'molbio/dao/AntibodyEngineeringDAO';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';

async function fetchPTMs(residues: string, callbackFn: (ptms: Array<PTMSite>) => void): Promise<void> {
  const [response] = await getPostTranslationalModifications([residues]);
  callbackFn(response);
}

async function fetchCDRs(
  residues: string,
  cdrDefinition: CDRDefinition,
  callbackFn: (cdrs: Array<CDRAnnotation>) => void
): Promise<void> {
  const [response] = await antibodyAnalysisStore.getCDRAnnotations([residues], cdrDefinition);
  if (response) {
    const regions = response
      .reduce((acc, cur) => acc.concat(cur.regions), [] as Array<CDRAnnotation>)
      .filter((r) => r.name.includes('CDR'));
    callbackFn(regions);
  }
}

const CDR_AWARE_SITE_TYPES = [LiabilitySiteType.AsparagineDeamidationInCDRs];

function filterByCDR(
  sites: Array<PTMSite>,
  cdrDefinition: CDRDefinition | undefined,
  cdrRegions: Array<CDRAnnotation>
): Array<PTMSite> {
  if (!cdrDefinition || cdrRegions.length === 0) {
    return sites.filter((site) => !CDR_AWARE_SITE_TYPES.includes(site.name));
  }

  return sites.filter(
    (site) =>
      !CDR_AWARE_SITE_TYPES.includes(site.name) ||
      cdrRegions.some((r) => r.start <= site.viewPos && site.viewPos < r.end)
  );
}

interface SiteProps {
  availableHeight: number;
  availableWidth: number;
  realToGap: Array<number>;
  site: {
    names: Array<LiabilitySiteType>;
    viewPos: number;
  };
  xFn: Props['xFn'];
}

function LiabilitySite({availableWidth, availableHeight, realToGap, site, xFn}: SiteProps): JSX.Element {
  const radius = Math.min(availableWidth, availableHeight) / 2;
  const verticalOffset = radius + availableHeight / 2;
  const tooltipText = site.names.map((name) => LIABILITY_SITE_DISPLAY_NAMES[name]).join('\n');

  // Breaking the component hierarchy and imperatively modifying DOM state like this is obviously hacky.
  // We decided to accept the tech debt here because (a) there is no ideal preexisting abstraction to manage
  // data like this across slices in a React-y way, and (b) we're only toggling CSS classes so the risk of
  // side effects seems low. Only works on protein maps (not protein alignments).
  const addClassNames = (): void => {
    const mapEl = document.querySelector('#aa-sequence-map');
    if (mapEl) {
      site.names.forEach((name) => {
        mapEl.classList.add(`highlight${name}`);
      });
    }
  };
  const removeClassNames = (): void => {
    const mapEl = document.querySelector('#aa-sequence-map');
    if (mapEl) {
      site.names.forEach((name) => {
        mapEl.classList.remove(`highlight${name}`);
      });
    }
  };

  return (
    <Tooltip title={tooltipText}>
      <g
        className={css(styles.noFocus)}
        role="img"
        aria-label={tooltipText}
        transform={`translate(${xFn(realToGap[site.viewPos]) + availableWidth / 2} ${verticalOffset})`}
        {...getTestProps({
          component: 'AaSequenceLiabilitySites',
          element: 'site',
          key: String(site.viewPos),
        })}
        data-amp-safe={false}
      >
        <circle
          className={`${css(styles.noFocus)} liability-site ${site.names.join(' ')}`}
          onMouseEnter={addClassNames}
          onMouseLeave={removeClassNames}
          r={radius}
          shapeRendering="geometricPrecision"
        />
      </g>
    </Tooltip>
  );
}

export interface Props {
  layout: AllPolymerLayouts['proteinLiabilitySites'];
  redraw: (boxHeight: number) => void;
  residues: string;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
  visibilityOptions: vo.VisibilityOptions;
}

export default function AaSequenceLiabilitySitesSlice(props: Props): JSX.Element | null {
  const {layout, redraw, residues, sliceStart, sliceEnd, visibilityOptions, xFn} = props;
  const realToGap = getRealToGapMap(residues);

  const [sites, setSites] = useState<Array<PTMSite>>([]);
  const [cdrRegions, setCDRRegions] = useState<Array<CDRAnnotation>>([]);
  const cdrDefinition = getAntibodyVisibilitySettings(visibilityOptions)[1];
  useEffect(() => {
    const ungappedResidues = removeGaps(residues);
    if (ungappedResidues.length > 0) {
      fetchPTMs(ungappedResidues, setSites);
      if (cdrDefinition) {
        fetchCDRs(ungappedResidues, cdrDefinition, setCDRRegions);
      }
    }
  }, [residues, cdrDefinition]);

  const availableWidth = xFn(sliceStart + 1) - xFn(sliceStart);
  const activeSiteTypes = Object.values(LiabilitySiteType).filter((site) =>
    visibilityOptions.getOption(vo.PROTEIN_LIABILITY_SITES.name)!.isSettingChecked(site)
  );

  let relevantSites = sites.filter(
    (site: PTMSite) =>
      site.viewPos >= sliceStart && site.viewPos < sliceEnd && activeSiteTypes.includes(site.name)
  );
  relevantSites = filterByCDR(relevantSites, cdrDefinition, cdrRegions);

  const boxHeight = layout.height + layout.vMargin;
  const siteCount = relevantSites.length;
  useEffect(() => {
    redraw(siteCount > 0 ? boxHeight : 0);
  }, [boxHeight, redraw, siteCount]);

  const groupedSites = Object.values(_.groupBy(relevantSites, (s) => s.viewPos)).map((group) => {
    const names = group.map((s) => s.name);
    const viewPos = group[0].viewPos;
    return {names, viewPos};
  });

  return (
    <g {...getTestProps({component: 'AaSequenceLiabilitySites', element: 'root'})}>
      {groupedSites.map((site) => (
        <LiabilitySite
          key={site.viewPos}
          {...{availableHeight: layout.height, availableWidth, realToGap, site, xFn}}
        />
      ))}
    </g>
  );
}

const styles = StyleSheet.create({
  noFocus: {
    ':focus': {
      outline: 'none',
    },
  },
});
